import React, { useEffect, useId, useRef } from 'react';
import { fontFamily } from '../../lib/fonts';

type Language = { tag: string; nativeName: string };
type Props = {
    languages: readonly Language[];
    selected: string;
    label: string;
    busy: boolean;
    variant: string;
    onSelect: (tag: string) => void;
};

// Decorative flags are presentation only: regional formatting still uses the
// user's full locale tag. Unknown future languages get a neutral globe.
const flags: Record<string, string> = { en: '🇺🇸', es: '🇪🇸' };
const css = `
.pz-language { position:relative; font-family:${fontFamily.regular}; color:#315438; }
.pz-language summary { list-style:none; display:flex; align-items:center; gap:4px;
  width:max-content; min-height:44px; box-sizing:border-box; padding:6px 8px;
  border:1px solid transparent; border-radius:10px; cursor:pointer;
  background:transparent; transition:background .12s, border-color .12s; }
.pz-language summary::-webkit-details-marker { display:none; }
.pz-language summary:hover,.pz-language[open] summary { background:#f0f5ef; border-color:#dce6d9; }
.pz-language summary:focus-visible,.pz-language button:focus-visible {
  outline:2px solid #577f48; outline-offset:3px; }
/* A fixed emoji box keeps flags centred on the text instead of riding its ascender. */
.pz-language__flag { display:inline-flex; align-items:center; justify-content:center;
  flex:none; width:22px; height:22px; font-size:17px; line-height:1;
  font-family:"Apple Color Emoji","Segoe UI Emoji","Noto Color Emoji",sans-serif; }
.pz-language summary .pz-language__flag { font-size:19px; }
.pz-language__chevron { flex:none; width:12px; height:12px; opacity:.65; transition:transform .12s; }
.pz-language[open] .pz-language__chevron { transform:rotate(180deg); }
.pz-language__menu { position:absolute; top:calc(100% + 6px); right:0;
  z-index:1000; width:172px; max-width:calc(100vw - 48px); padding:4px;
  box-sizing:border-box; border:1px solid #dce6d9; border-radius:12px;
  background:#fff; box-shadow:0 10px 28px rgba(32,62,39,.12);
  max-height:min(320px,50vh); overflow-y:auto; }
.pz-language[data-variant=mobile] .pz-language__menu,
.pz-language[data-variant=settings] .pz-language__menu { right:auto; left:0; }
.pz-language button { display:flex; align-items:center; gap:10px; width:100%;
  min-height:40px; padding:8px 10px; border:0; border-radius:8px; background:transparent;
  font-family:inherit; font-size:14px; line-height:20px; font-weight:500;
  text-align:left; color:#315438; cursor:pointer; }
.pz-language button + button { margin-top:2px; }
.pz-language[data-variant=mobile] button { min-height:44px; }
.pz-language button:hover { background:#f5f7f3; }
.pz-language button[aria-pressed=true] { background:#edf3e9; color:#1d3824; font-weight:600; }
.pz-language button:disabled { cursor:wait; opacity:.6; }
.pz-language__check { flex:none; margin-left:auto; width:14px; height:14px; color:#4f7a47; }
@media (prefers-reduced-motion:reduce) { .pz-language summary,.pz-language__chevron { transition:none; } }
`;

/** Web-only view. Native keeps its existing control in LanguageSelector. */
export default function LanguageDropdown({ languages, selected, label, busy, variant, onSelect }: Props) {
    const root = useRef<HTMLDetailsElement>(null);
    const menuId = useId();
    const active = languages.find((language) => language.tag === selected) ?? languages[0];
    useEffect(() => {
        const closeOutside = (event: PointerEvent) => {
            if (root.current && !root.current.contains(event.target as Node)) root.current.open = false;
        };
        const closeOnEscape = (event: KeyboardEvent) => {
            if (event.key === 'Escape' && root.current?.open) {
                event.preventDefault();
                root.current.open = false;
                root.current.querySelector('summary')?.focus();
            }
        };
        document.addEventListener('pointerdown', closeOutside);
        document.addEventListener('keydown', closeOnEscape);
        return () => {
            document.removeEventListener('pointerdown', closeOutside);
            document.removeEventListener('keydown', closeOnEscape);
        };
    }, []);
    if (!active) return null;
    return (
        <details ref={root} className="pz-language" data-variant={variant}
            data-testid={`language-dropdown-${variant}`}
            onBlur={(event) => {
                // WebKit may not focus a clicked button. Closing on its null
                // blur target hides the option before click can select it.
                // Outside pointer presses and Escape are handled above.
                if (event.relatedTarget && !event.currentTarget.contains(event.relatedTarget as Node)) {
                    event.currentTarget.open = false;
                }
            }}>
            <style>{css}</style>
            <summary aria-label={`${label}: ${active.nativeName}`} aria-controls={menuId} aria-busy={busy}>
                <span className="pz-language__flag" aria-hidden="true">{flags[active.tag] ?? '🌐'}</span>
                <svg className="pz-language__chevron" viewBox="0 0 12 12" aria-hidden="true">
                    <path d="m3 4.5 3 3 3-3" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
            </summary>
            <div id={menuId} className="pz-language__menu" role="group" aria-label={label}>
                {languages.map((language) => (
                    <button key={language.tag} type="button" disabled={busy}
                        aria-label={language.nativeName} aria-pressed={language.tag === selected}
                        data-testid={`language-selector-${variant}-${language.tag}`}
                        onClick={() => {
                            if (root.current) {
                                root.current.open = false;
                                root.current.querySelector('summary')?.focus();
                            }
                            onSelect(language.tag);
                        }}>
                        <span className="pz-language__flag" aria-hidden="true">{flags[language.tag] ?? '🌐'}</span>
                        <span>{language.nativeName}</span>
                        {language.tag === selected && (
                            <svg className="pz-language__check" viewBox="0 0 16 16" aria-hidden="true">
                                <path d="m3 8 3 3 7-7" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                        )}
                    </button>
                ))}
            </div>
        </details>
    );
}
